import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type {
  exportTableColumnValidation,
  tableColumnValidation,
  TableSettingColumn,
} from 'types/tableSettingsTypes';

interface AdminOrderState {
  excludedFilter: string[];
  filterData: Record<string, any>;
  reloadTableData: boolean;
  tableColumnValidation: tableColumnValidation;
  defaultColumnList: TableSettingColumn[];
  tableColumnList: TableSettingColumn[];
  exportTableColumnValidation: exportTableColumnValidation;
  exportTableColumnList: TableSettingColumn[];
  orderDetailExportTableColumnValidation: exportTableColumnValidation;
  orderDetailExportTableColumnList: TableSettingColumn[];
  filterColumnOrder: string[];
}
const defaultColumnList = [
  {
    name: 'order_number',
    label_key: 'order_id',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 1,
  },
  {
    name: 'user',
    label_key: 'customer',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 2,
  },
  {
    name: 'ffl',
    label_key: 'from_ffl',
    allow_list: true,
    allow_filter: true,
    show_list_checkbox: true,
    show_filter_checkbox: true,
    type: 'asyncselect',
    filter_placeholder: 'ffl_all',
    asyncKey: 'ffl',
    option_value_key: 'id',
    option_label_key: 'business_name',
    multi: false,
    order: 3,
  },
  {
    name: 'shipping_ffl',
    label_key: 'to_ffl',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 4,
  },
  {
    name: 'items',
    label_key: 'items',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 5,
  },
  {
    name: 'total',
    label_key: 'total',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 5,
  },
  {
    name: 'payment_status',
    label_key: 'payment',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 6,
  },
  {
    name: 'status',
    label_key: 'order_status',
    allow_list: true,
    allow_filter: true,
    show_list_checkbox: true,
    show_filter_checkbox: true,
    type: 'select',
    filter_placeholder: 'status_all',
    selectKey: 'order_status',
    option_value_key: 'slug',
    option_label_key: 'name',
    multi: false,
    order: 6,
  },
  {
    name: 'category',
    label_key: 'category',
    allow_list: false,
    allow_filter: true,
    show_list_checkbox: false,
    show_filter_checkbox: true,
    type: 'asyncselect',
    filter_placeholder: 'category_all',
    asyncKey: 'productCategories',
    option_value_key: 'id',
    option_label_key: 'name',
    multi: false,
    order: 8,
  },
];
const defaultConfig = {
  allow_list: true,
  allow_filter: false,
  show_list_checkbox: true,
  show_filter_checkbox: false,
};

const fields = [
  { key: 'user_name', label: 'customer_name' },
  { key: 'user_email', label: 'customer_email' },
  { key: 'order_number', label: 'order_number' },
  { key: 'order_status', label: 'order_status' },
  { key: 'billing_address', label: 'billing_address' },
  { key: 'shipping_address', label: 'shipping_address' },
  { key: 'product_name', label: 'product_name' },
  { key: 'product_type', label: 'product_type' },
  { key: 'quantity', label: 'quantity' },
  { key: 'unit_price', label: 'price' },
  { key: 'subtotal', label: 'subtotal' },
  { key: 'discount_amount', label: 'discount' },
  { key: 'shipping_amount', label: 'shipping' },
  { key: 'tax_amount', label: 'tax' },
  { key: 'total_amount', label: 'Total' },
  { key: 'order_item_status', label: 'order_item_status' },
  { key: 'shipping_ffl', label: 'shipping_ffl' },
  { key: 'shipping_ffl_number', label: 'shipping_ffl_number' },
  { key: 'shipping_ffl_address', label: 'shipping_ffl_address' },
];

const exportColumnList = fields.map((field, index) => ({
  name: field.key,
  label_key: field.label,
  order: index + 1,
  ...defaultConfig,
}));

// Single-order export (order detail page > More actions > Download Order Data). One row per
// order item, with the order level columns repeated - the column names must match the keys
// the /export-order-detail-csv formatter builds.
const orderDetailExportFields = [
  { key: 'order_number', label: 'order_number' },
  { key: 'order_status', label: 'order_status' },
  { key: 'payment_status', label: 'payment_status' },
  { key: 'placed_at', label: 'placed_on' },
  { key: 'customer_name', label: 'customer_name' },
  { key: 'customer_email', label: 'customer_email' },
  { key: 'customer_mobile', label: 'customer_mobile' },
  { key: 'ffl_business_name', label: 'ffl' },
  { key: 'ffl_number', label: 'ffl_number' },
  { key: 'product_name', label: 'product_name' },
  { key: 'product_sku', label: 'sku' },
  { key: 'product_type', label: 'product_type' },
  { key: 'quantity', label: 'quantity' },
  { key: 'listed_unit_price', label: 'listed_unit_price' },
  { key: 'unit_price', label: 'price' },
  { key: 'offer_price', label: 'offer_price' },
  { key: 'offer_discount_amount', label: 'offer_discount' },
  { key: 'item_subtotal', label: 'item_subtotal' },
  { key: 'item_discount_amount', label: 'item_discount' },
  { key: 'item_shipping_amount', label: 'item_shipping' },
  { key: 'item_tax_amount', label: 'item_tax' },
  { key: 'item_total_amount', label: 'item_total' },
  { key: 'item_status', label: 'order_item_status' },
  { key: 'delivery_type', label: 'delivery_type' },
  { key: 'tracking_number', label: 'tracking_number' },
  { key: 'carrier', label: 'carrier' },
  { key: 'shipping_service_code', label: 'shipping_service' },
  { key: 'shipping_ffl', label: 'shipping_ffl' },
  { key: 'shipping_address', label: 'shipping_address' },
  { key: 'billing_address', label: 'billing_address' },
  { key: 'order_subtotal', label: 'order_subtotal' },
  { key: 'order_discount_amount', label: 'order_discount' },
  { key: 'order_offer_discount_amount', label: 'order_offer_discount' },
  { key: 'order_shipping_amount', label: 'order_shipping' },
  { key: 'order_tax_amount', label: 'order_tax' },
  { key: 'order_total_amount', label: 'order_total' },
  { key: 'currency', label: 'currency' },
  { key: 'notes', label: 'notes' },
];

const orderDetailExportColumnList = orderDetailExportFields.map((field, index) => ({
  name: field.key,
  label_key: field.label,
  order: index + 1,
  ...defaultConfig,
}));
const initialState: AdminOrderState = {
  excludedFilter: ['ffl_verified', 'search'],
  filterData: {},
  reloadTableData: false,
  tableColumnValidation: {
    minListColumn: 2,
    maxListColumn: 8,
    minFilterColumn: 0,
    maxFilterColumn: 8,
  },
  defaultColumnList: defaultColumnList,
  tableColumnList: JSON.parse(JSON.stringify(defaultColumnList)),
  exportTableColumnValidation: {
    minListColumn: 2,
  },
  exportTableColumnList: exportColumnList,
  orderDetailExportTableColumnValidation: {
    minListColumn: 2,
  },
  orderDetailExportTableColumnList: orderDetailExportColumnList,
  // filter dropdowns, left to right (FFL users don't get the ffl one)
  filterColumnOrder: ['status', 'ffl', 'category'],
};

const adminOrderSlice = createSlice({
  name: 'adminOrder',
  initialState,
  reducers: {
    setFilterData(state, action: PayloadAction<Record<string, any>>) {
      state.filterData = action.payload;
    },
    removeFilterKey(state, action: PayloadAction<string>) {
      delete state.filterData[action.payload];
    },
    resetFilterData(state) {
      state.filterData = {};
    },
    setReloadTableData(state, action: PayloadAction<boolean>) {
      state.reloadTableData = action.payload;
    },
    setOrderTableColumnList(state, action: PayloadAction<TableSettingColumn[]>) {
      state.tableColumnList = action.payload;
    },
  },
});

export const {
  setFilterData,
  removeFilterKey,
  resetFilterData,
  setReloadTableData,
  setOrderTableColumnList,
} = adminOrderSlice.actions;

export default adminOrderSlice.reducer;
